import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * A status badge.
 *
 * Every tone carries a dot as well as a colour, so the state survives a
 * greyscale print and a reader who cannot separate red from green. `elevated`
 * is reserved for access that confers elevated privilege — superuser, system
 * groups, administrative grants — and nothing else uses amber, so a list scan
 * lands straight on what carries risk.
 */
const badgeVariants = cva(
  "inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-footnote font-medium",
  {
    variants: {
      tone: {
        neutral: "bg-surface-inset text-fg-muted",
        muted: "border border-line text-fg-subtle",
        brand: "bg-brand-100 text-brand-800 dark:bg-brand-700/25 dark:text-brand-200",
        accent: "bg-accent-soft text-accent",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning",
        danger: "bg-danger-soft text-danger",
        info: "bg-info-soft text-info",
        elevated: "bg-warning-soft text-warning",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

const DOTLESS = new Set(["neutral", "muted", "brand", "accent"]);

export function Badge({
  className,
  tone,
  dot,
  icon,
  children,
  ...props
}: ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    /** Defaults to on for the state tones, off for the descriptive ones. */
    dot?: boolean;
    icon?: ReactNode;
  }) {
  const showDot = dot ?? !DOTLESS.has(tone ?? "neutral");
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...props}>
      {icon ??
        (showDot ? (
          <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
        ) : null)}
      <span className="truncate">{children}</span>
    </span>
  );
}
